import { useState, useEffect } from 'react'
import { supabaseGetter } from '~/api/supabase-getter'
import { useCropsContext } from '~/common/context/crops/crops-context'

/**
 * Fetches existing tasks for the selected entity and returns them in
 * scheduleDataOverride format with readOnly: true on each task.
 *
 * @param {string} targetType - 'org' | 'crop'
 * @param {Object|null} selectedEntity - The selected org or crop object
 * @returns {{ existingTasksData: Array, loading: boolean }}
 */
export const useExistingTasksData = ({ targetType, selectedEntity }) => {
  const [existingTasksData, setExistingTasksData] = useState([])
  const [loading, setLoading] = useState(false)
  const { refreshKey } = useCropsContext()

  useEffect(() => {
    if (!selectedEntity) {
      setExistingTasksData([])
      setLoading(false)
      return
    }

    async function fetchTasks() {
      setLoading(true)

      try {
        let tasks = []

        if (targetType === 'org') {
          const { data, error } = await supabaseGetter.tasks.getOrgTasksByOrganizations([selectedEntity.id])
          if (error) throw error
          tasks = data || []
        } else if (targetType === 'crop') {
          const { data, error } = await supabaseGetter.tasks.getByCrop(selectedEntity.id)
          if (error) throw error
          tasks = data || []
        }

        if (tasks.length === 0) {
          setExistingTasksData([])
          return
        }

        // Resolve entity display name
        const entityName =
          targetType === 'org'
            ? selectedEntity.name
            : selectedEntity.crop_templates?.name || selectedEntity.name || selectedEntity.id

        const entityOrgId =
          targetType === 'org' ? selectedEntity.id : selectedEntity.organization_id

        const now = new Date().toISOString()
        const year = new Date().getFullYear()

        const existingRow = {
          crop: {
            id: selectedEntity.id,
            name: entityName,
            template_crop_species: { id: selectedEntity.id, name: entityName },
            count: 1,
            organization_id: entityOrgId,
          },
          schedule: {
            id: `existing-schedule-${selectedEntity.id}`,
            crop_id: selectedEntity.id,
            year,
            planting_date: now,
            expected_harvest_date: now,
            state: 'active',
          },
          phases: [
            {
              id: `existing-phase-${selectedEntity.id}`,
              phase_type_code: 'maturing',
              phase_type: { code: 'maturing', name: 'Maturing' },
              est_started_at: now,
              est_duration: '365 days',
              step: 1,
              state: 'scheduled',
            },
          ],
          tasks: tasks.map(t => ({
            id: t.id,
            task_code: t.task_code,
            // Normalize task_types (plural, from DB join) → task_type (singular, expected by timeline-task-utils)
            task_type: { name: t.task_types?.name || t.task_code },
            task_window_start: t.task_window_start,
            duration: t.duration,
            state: t.state,
            notes: t.notes,
            organization_id: t.organization_id,
            crop_id: t.crop_id,
            readOnly: true,
          })),
        }

        setExistingTasksData([existingRow])
      } catch (err) {
        console.error('[useExistingTasksData] fetch error:', err)
        setExistingTasksData([])
      } finally {
        setLoading(false)
      }
    }

    fetchTasks()
  }, [targetType, selectedEntity, refreshKey])

  return { existingTasksData, loading }
}
